import { useEffect, useMemo, useState } from 'react';
import { fetchProfile } from '../api/profileApi';
import { useAuth } from '../auth/AuthContext';
import { getStoredLoginIdentifier, getSessionToken } from '../auth/sessionToken';
import { useI18n } from '../i18n';
import { extractProfileDisplaySummary } from '../utils/profileDisplaySummary';
import {
  getStoredProfileDetailsHints,
  joinedProfileDetailsDisplayName,
  persistProfileDetailsHints,
} from '../utils/profileDetailsStorage';
import {
  resolveHeaderDisplayName,
  shouldSyncUserNameFromProfile,
} from '../utils/headerDisplayName';

/** Top-bar greeting: profile name when available, else login identifier. */
export function useHeaderDisplayName(): string {
  const { t } = useI18n();
  const { isLoggedIn, userName, updateUserDisplayName } = useAuth();
  const [profileDisplayName, setProfileDisplayName] = useState<string | null>(null);
  const loginId = getStoredLoginIdentifier();
  const cachedNameFromHints = useMemo(
    () => joinedProfileDetailsDisplayName(getStoredProfileDetailsHints(loginId)),
    [isLoggedIn, loginId, userName],
  );

  useEffect(() => {
    if (!isLoggedIn) {
      setProfileDisplayName(null);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const data = await fetchProfile();
        if (cancelled) {
          return;
        }
        const summary = extractProfileDisplaySummary(
          data,
          userName,
          loginId,
          getSessionToken(),
        );
        const name = summary.displayName?.trim() || null;
        setProfileDisplayName(name);
        persistProfileDetailsHints(
          {
            firstName: summary.firstName,
            lastName: summary.lastName,
            occupation: summary.occupation,
            birthDate: summary.dateOfBirth,
          },
          loginId,
        );
        if (name && shouldSyncUserNameFromProfile(userName, name)) {
          updateUserDisplayName(name);
        }
      } catch {
        if (!cancelled) {
          setProfileDisplayName(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, loginId, userName, updateUserDisplayName]);

  return resolveHeaderDisplayName(profileDisplayName, userName, cachedNameFromHints) || t('header.guest');
}
